<div class="session-form-shell">
  <!-- ── Top bar ──────────────────────────────────────────────────────────────── -->
  <div class="form-topbar">
    <button class="btn-back" (click)="goBack()"><span class="btn-back-arrow">←</span> Sessions</button>
    <div class="topbar-center">
      <span class="form-title">{{ pageTitle }}</span>
      @if (mode === 'review' && session) {
        <span class="queue-badge">{{ total }} remaining</span>
      }
    </div>
  </div>

  @if (loading) {
    <app-loader></app-loader>
  }

  <!-- ── TSV paste (new session entry point) ─────────────────────────────────── -->
  @if (showPasteEntryPoint) {
    <div class="paste-shell">
      <div class="paste-card">
        <h2 class="paste-title">New Curriculum Session</h2>

        <!-- Saving state -->
        @if (saving) {
          <p class="paste-description">Saving sessions...</p>
          <app-loader></app-loader>
        }

        <!-- Paste textarea (default state) -->
        @if (showPasteInputState) {
          <p class="paste-description">Paste one or more data rows from the curriculum Google Sheet. No need to include the header row.</p>
          <textarea
            class="paste-textarea"
            [(ngModel)]="pasteText"
            placeholder="Paste one or more data rows from Google Sheets here..."
            rows="10"
          ></textarea>
          @if (pasteError) {
            <div class="field-error">{{ pasteError }}</div>
          }
          @if (saveError) {
            <div class="field-error">⚠ {{ saveError }}</div>
          }
          <div class="paste-actions">
            <button class="btn-primary" (click)="parseTsv()" [disabled]="!pasteText.trim()">Parse &amp; Continue →</button>
          </div>
          <div class="paste-divider"><span>or</span></div>
          <button class="btn-link" type="button" (click)="switchToManual()">Create manually without pasting</button>
        }

        <!-- Bulk save success state -->
        @if (showBulkPasteSuccessState) {
          <p class="paste-success">✓ {{ bulkSaveSuccessCount }} session{{ bulkSaveSuccessCount !== 1 ? 's' : '' }} successfully added.</p>
          <div class="paste-divider"><span>or</span></div>
          <button class="btn-link" type="button" (click)="resetBulkPasteState()">Paste again</button>
        }
      </div>
    </div>
  }

  <!-- ── Review mode: empty state ─────────────────────────────────────────────── -->
  @if (showReviewEmptyState) {
    <div class="empty-state">
      <div class="empty-icon">✓</div>
      <h2>All caught up!</h2>
      <p>No unreviewed sessions remain in the queue.</p>
      <button class="btn-primary" (click)="goBack()">← Back to sessions</button>
    </div>
  }

  <!-- ── Session form ──────────────────────────────────────────────────────────── -->
  @if (showSessionView) {
    <div class="session-view">
      <!-- Details card -->
      <div class="details-card">
        <h3 class="details-heading">Session Details</h3>

        <div class="details-grid">
          <div class="field-group">
            <label class="field-label">Unit ID</label>
            <input type="text" class="field-input" [(ngModel)]="session.unitId" (ngModelChange)="checkDirty()" placeholder="Unit UUID" />
            @if (emptyMandatoryFields.includes('unitId')) {
              <div class="field-error">This field is required.</div>
            }
            @if (parsedUnitIntId) {
              <div class="field-hint">
                Sheet Unit ID: <strong>{{ parsedUnitIntId }}</strong> — paste the corresponding UUID above.
              </div>
            }
          </div>

          <div class="field-group">
            <label class="field-label">Unit Title</label>
            @if (session.unitTitle) {
              <div class="field-readonly">{{ session.unitTitle }}</div>
            } @else {
              <div class="field-readonly subtitle">Will be populated after saving.</div>
            }
          </div>

          <div class="field-group field-group--narrow">
            <label class="field-label">Session Order</label>
            <input type="number" class="field-input" [(ngModel)]="session.sessionOrder" (ngModelChange)="checkDirty()" min="1" />
            @if (emptyMandatoryFields.includes('sessionOrder')) {
              <div class="field-error">This field is required and must be at least 1.</div>
            }
          </div>

          <div class="field-group">
            <label class="field-label">Status</label>
            <span class="status-badge" [class.status-approved]="session.clinicalApproved" [class.status-unreviewed]="!session.clinicalApproved">
              {{ session.clinicalApproved ? 'Approved' : 'Unreviewed' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Section editors -->
      @for (key of sectionKeys; track key; let i = $index) {
        <ng-container
          [ngTemplateOutlet]="sectionCard"
          [ngTemplateOutletContext]="{ key: key, stepNum: i + 1, stepOf: sectionKeys.length }"
        ></ng-container>
      }
    </div>
  }

  <!-- ── Action bar ────────────────────────────────────────────────────────────── -->
  @if (showActionBar) {
    <div class="action-bar">
      @if (saveError) {
        <div class="save-error">⚠ {{ saveError }}</div>
      }

      @if (mode === 'review') {
        <button class="btn-skip" (click)="skip()">Skip →</button>
        <div class="action-bar-right">
          <div class="approve-wrap">
            <button class="btn-approve" (click)="approve()" [disabled]="saving">
              @if (!saving) {
                <span>✓ &nbsp;{{ isDirty ? 'Save changes and Approve Session' : 'Approve Session' }}</span>
              } @else {
                <span>Saving...</span>
              }
            </button>
            @if (isDirty) {
              <p class="unsaved-notice">ⓘ You have unsaved changes.</p>
            } @else {
              <p class="unsaved-notice">&nbsp;</p>
            }
          </div>
        </div>
      }

      @if (savedWithPendingAudio) {
        <div class="tts-saved-bar" [class.ready]="ttsAllReady">
          <span class="tts-saved-text">
            {{ ttsAllReady ? 'Saved — spoken audio is ready.' : 'Saved. Generating spoken audio…' }}
          </span>
          <button class="btn-link" type="button" (click)="goBack()">Back to list</button>
        </div>
      }

      @if (mode === 'editor') {
        <div class="editor-actions">
          <button class="btn-primary" (click)="save()" [disabled]="saving">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
          @if (isExistingEditor) {
            <button class="btn-danger" type="button" (click)="confirmDelete()">Delete</button>
          }
          <button class="btn-link" type="button" (click)="goBack()">Cancel</button>
        </div>
      }
    </div>
  }
</div>

<!-- ─── Section card template ──────────────────────────────────────────────────── -->
<ng-template #sectionCard let-key="key" let-stepNum="stepNum" let-stepOf="stepOf">
  <div class="section-card">
    <div class="section-card-head" (click)="toggleSection(key)" style="cursor: pointer">
      <span class="step-num">{{ stepNum }}</span>
      <span class="step-icon">{{ sectionIcons[key] }}</span>
      <span class="step-title">{{ sectionLabels[key] }}</span>
      @if (stepOf > 1) {
        <span class="step-count">Step {{ stepNum }} of {{ stepOf }}</span>
      }
      <span class="collapse-toggle">{{ isSectionCollapsed(key) ? '▸' : '▾' }}</span>
    </div>

    @if (!isSectionCollapsed(key)) {
      <div class="section-card-body">
        <!-- ── coreMaterial: integer FK to Content.id ───────────────────────────── -->
        @if (key === 'coreMaterial') {
          <label class="field-label">Content ID</label>
          <input
            type="number"
            class="field-input"
            placeholder="Content integer ID (e.g. 2372)"
            [(ngModel)]="sectionDrafts['coreMaterial']['contentId']"
            (ngModelChange)="checkDirty()"
            min="1"
          />
        }

        <!-- ── extend: integer FK to Content.id + session notes ─────────────────── -->
        @if (key === 'extend') {
          <div class="field-group">
            <label class="field-label">Content ID</label>
            <input
              type="number"
              class="field-input"
              placeholder="Content integer ID (e.g. 2372)"
              [(ngModel)]="sectionDrafts['extend']['contentId']"
              (ngModelChange)="checkDirty()"
              min="1"
            />
          </div>
          <div class="field-group">
            <div class="field-label-row">
              <label class="field-label">Session Note</label>
              <tts-status-chip fieldPath="sessionNote" (open)="openTtsPanel($event)"></tts-status-chip>
            </div>
            <textarea
              class="field-textarea"
              [(ngModel)]="session.sessionNote"
              (ngModelChange)="checkDirty()"
              placeholder="Lesson summary for the educator"
              maxlength="500"
            ></textarea>
          </div>
          <div class="field-group">
            <div class="field-label-row">
              <label class="field-label">Session Note Tip</label>
              <tts-status-chip fieldPath="sessionNoteTip" (open)="openTtsPanel($event)"></tts-status-chip>
            </div>
            <textarea
              class="field-textarea"
              [(ngModel)]="session.sessionNoteTip"
              (ngModelChange)="checkDirty()"
              placeholder="Take-home tip for caregivers"
              maxlength="500"
            ></textarea>
          </div>
        }

        <!-- ── Practice: fixed 3-entry typed array ──────────────────────────────── -->
        @if (key === 'practice') {
          <div class="practice-entry-list">
            @for (entry of practiceDrafts; track $index; let i = $index) {
              <div class="practice-entry-card">
                <div class="practice-entry-header">
                  <span class="practice-entry-index">{{ i + 1 }}</span>
                  <span class="practice-entry-label">{{ entry.type }}</span>
                </div>
                <div class="practice-entry-fields">
                  <div class="field-group">
                    <label class="field-label">Title</label>
                    <input
                      type="text"
                      class="field-input"
                      [(ngModel)]="practiceDrafts[i].title"
                      (ngModelChange)="checkDirty()"
                      placeholder="Activity title"
                    />
                  </div>
                  <div class="field-group">
                    <div class="field-label-row">
                      <label class="field-label">Prompt</label>
                      <tts-status-chip
                        [fieldPath]="practiceFieldPath(practiceDrafts[i].type)"
                        (open)="openTtsPanel($event)"
                      ></tts-status-chip>
                    </div>
                    <textarea
                      class="field-textarea"
                      [(ngModel)]="practiceDrafts[i].prompt"
                      (ngModelChange)="checkDirty()"
                      placeholder="Activity prompt"
                    ></textarea>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        <!-- ── Session Opener ──────────────────────────────────── -->
        @if (key === 'opener') {
          <div class="field-group">
            <div class="field-label-row">
              <label class="field-label">Guiding Question</label>
              <tts-status-chip fieldPath="opener.guidingQuestion" (open)="openTtsPanel($event)"></tts-status-chip>
            </div>
            <textarea class="field-textarea" [(ngModel)]="sectionDrafts['opener']['guidingQuestion']" (ngModelChange)="checkDirty()"></textarea>
          </div>
          <div class="key-terms-section">
            <h6 class="key-terms-heading">Key Terms</h6>
            @if (openerKeyTermsDrafts.length === 0) {
              <p class="empty-section" style="margin-bottom: 12px">No key terms yet.</p>
            }
            @for (kt of openerKeyTermsDrafts; track kt.id; let i = $index) {
              <div class="key-term-card" style="margin-bottom: 10px">
                <div class="key-term-card-header">
                  <span class="key-term-index">{{ i + 1 }}</span>
                  <button class="btn-remove" (click)="removeKeyTerm(i)" type="button" title="Remove">✕</button>
                </div>
                <div class="field-label-row">
                  <label class="field-label">Title</label>
                  <tts-status-chip
                    [fieldPath]="keyTermFieldPath(kt, 'title')"
                    (open)="openTtsPanel($event)"
                  ></tts-status-chip>
                </div>
                <input
                  type="text"
                  class="field-input"
                  [(ngModel)]="openerKeyTermsDrafts[i].title"
                  (ngModelChange)="checkDirty()"
                  placeholder="Key term title"
                />
                <div class="field-label-row" style="margin-top: 10px">
                  <label class="field-label">Description</label>
                  <tts-status-chip
                    [fieldPath]="keyTermFieldPath(kt, 'description')"
                    (open)="openTtsPanel($event)"
                  ></tts-status-chip>
                </div>
                <textarea
                  class="field-textarea"
                  [(ngModel)]="openerKeyTermsDrafts[i].description"
                  (ngModelChange)="checkDirty()"
                  placeholder="Key term description"
                ></textarea>
              </div>
            }
            <button class="btn-secondary" (click)="addKeyTerm()" type="button">+ Add Key Term</button>
          </div>
        }

        @if (sectionErrors[key]) {
          <div class="field-error">{{ sectionErrors[key] }}</div>
        }
      </div>
    }
  </div>
</ng-template>

<!-- ─── Spoken audio panel ─────────────────────────────────────────────────────── -->
<tts-audio-panel
  [fieldPath]="activeTtsFieldPath"
  [label]="ttsLabelFor(activeTtsFieldPath)"
  [text]="ttsTextFor(activeTtsFieldPath)"
  [formDirty]="isDirty"
  (close)="closeTtsPanel()"
></tts-audio-panel>
